import { useState, useEffect, useRef, useCallback } from 'react';
import {
  previewCareerExperiencesFromResume,
  deduplicateCareerExperiences,
  createCareerExperience,
  updateCareerExperience,
  deleteCareerExperience,
  ProposedCareerExperience,
  DeduplicateResult,
} from '../../services/careerMemory';
import './ResumeImportModal.css';

/**
 * _kind:    'new'     → will createCareerExperience
 *           'merge'   → will updateCareerExperience(_existingId)
 *           'cleanup' → will updateCareerExperience(_existingId) + delete _deleteIds
 */
interface EditableExperience extends ProposedCareerExperience {
  _id: string;
  _removed: boolean;
  _kind: 'new' | 'merge' | 'cleanup';
  _existingId?: string;
  _deleteIds?: string[];
}

type Phase = 'loading' | 'review' | 'saving' | 'done' | 'error';

interface ResumeImportModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export function ResumeImportModal({ onClose, onSaved }: ResumeImportModalProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [elapsed, setElapsed] = useState(0);
  const [extractionDone, setExtractionDone] = useState(false);
  const [dedupDone, setDedupDone] = useState(false);
  const [items, setItems] = useState<EditableExperience[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [errorMsg, setErrorMsg] = useState('');
  const [savedCount, setSavedCount] = useState(0);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step display: time-based for early steps, completion-flag for last two
  const steps = [
    { label: 'Reading your resume',              isDone: () => elapsed >= 2 },
    { label: 'Structuring work history',         isDone: () => elapsed >= 5 },
    { label: 'Building STAR narratives',         isDone: () => elapsed >= 10 },
    { label: 'Enriching with context',           isDone: () => elapsed >= 18 },
    { label: 'Finalizing career timeline',       isDone: () => extractionDone },
    { label: 'Checking memory for duplicates',   isDone: () => dedupDone },
  ];

  const stepDone  = (i: number) => steps[i].isDone();
  const stepActive = (i: number) => !stepDone(i) && (i === 0 || stepDone(i - 1));

  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);

    let cancelled = false;

    (async () => {
      try {
        // ── Step 1: extract from resume ──────────────────────────────
        const result = await previewCareerExperiencesFromResume();
        if (cancelled) return;

        const extracted = Array.isArray(result.extractedExperiences)
          ? result.extractedExperiences
          : [];

        if (!extracted.length) {
          if (timerRef.current) clearInterval(timerRef.current);
          setErrorMsg(result.message || 'No experiences could be extracted from your resume.');
          setPhase('error');
          return;
        }

        setExtractionDone(true);

        // ── Step 2: dedup against existing memory ────────────────────
        let dedup: DeduplicateResult;
        try {
          dedup = await deduplicateCareerExperiences(extracted);
        } catch (dedupErr) {
          // Dedup failing is non-fatal — treat everything as new
          console.warn('Dedup failed, treating all as new:', dedupErr);
          dedup = {
            internalGroups: [],
            crossMatches: [],
            newProposedIndices: extracted.map((_, i) => i),
            skipped: true,
          };
        }

        if (cancelled) return;
        if (timerRef.current) clearInterval(timerRef.current);
        setDedupDone(true);

        // ── Build editable items ──────────────────────────────────────
        const ts = Date.now();
        const editables: EditableExperience[] = [];

        // New experiences (no match found)
        for (const idx of dedup.newProposedIndices) {
          const exp = extracted[idx];
          if (!exp) continue;
          editables.push({
            ...exp,
            _id: `new-${ts}-${idx}`,
            _removed: false,
            _kind: 'new',
          });
        }

        // Cross-matches: proposed merges with existing
        for (const match of dedup.crossMatches) {
          const base = extracted[match.proposedIndex] || {};
          editables.push({
            ...base,
            ...match.merged,
            _id: `merge-${ts}-${match.proposedIndex}`,
            _removed: false,
            _kind: 'merge',
            _existingId: match.existingId,
          });
        }

        // Internal duplicates among existing experiences
        for (let gi = 0; gi < dedup.internalGroups.length; gi++) {
          const group = dedup.internalGroups[gi];
          editables.push({
            ...group.merged,
            _id: `cleanup-${ts}-${gi}`,
            _removed: false,
            _kind: 'cleanup',
            _existingId: group.keepId,
            _deleteIds: group.removeIds,
          });
        }

        if (!editables.length) {
          setErrorMsg('No experiences could be processed.');
          setPhase('error');
          return;
        }

        setItems(editables);
        setPhase('review');
      } catch (err: any) {
        if (cancelled) return;
        if (timerRef.current) clearInterval(timerRef.current);
        setErrorMsg(err?.message || 'Failed to extract experiences from resume.');
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const updateItem = (id: string, field: keyof ProposedCareerExperience, value: string) => {
    setItems((prev) =>
      prev.map((item) => (item._id === id ? { ...item, [field]: value } : item))
    );
  };

  const removeItem = (id: string) => {
    setItems((prev) =>
      prev.map((item) => (item._id === id ? { ...item, _removed: true } : item))
    );
  };

  const activeItems = items.filter((item) => !item._removed);
  const newCount     = activeItems.filter((i) => i._kind === 'new').length;
  const mergeCount   = activeItems.filter((i) => i._kind === 'merge').length;
  const cleanupCount = activeItems.filter((i) => i._kind === 'cleanup').length;

  const buildPayload = (item: EditableExperience) => ({
    title: item.title,
    description: item.description,
    startDate: item.startDate,
    endDate: item.endDate,
    company: item.company,
    role: item.role,
    type: item.type,
    situation: item.situation,
    task: item.task,
    action: item.action,
    result: item.result,
    skills: item.skills,
    technologies: item.technologies,
    metrics: item.metrics,
    tags: Array.from(new Set([...(item.skills || []), ...(item.technologies || [])])).slice(0, 10),
  });

  const handleSave = async () => {
    if (!activeItems.length) return;
    setPhase('saving');
    setErrorMsg('');
    setSavingProgress({ current: 0, total: activeItems.length });

    try {
      for (let i = 0; i < activeItems.length; i++) {
        setSavingProgress({ current: i + 1, total: activeItems.length });
        const item = activeItems[i];
        const payload = buildPayload(item);

        if (item._kind === 'new') {
          await createCareerExperience(payload);
        } else if (item._kind === 'merge' && item._existingId) {
          await updateCareerExperience(item._existingId, payload);
        } else if (item._kind === 'cleanup' && item._existingId) {
          await updateCareerExperience(item._existingId, payload);
          for (const delId of item._deleteIds || []) {
            await deleteCareerExperience(delId);
          }
        }
      }
      setSavedCount(activeItems.length);
      setPhase('done');
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to save some experiences. Please try again.');
      setPhase('review');
    }
  };

  const getTypeIcon = (type?: string) => {
    switch (type) {
      case 'project':     return '🚀';
      case 'success':     return '✨';
      case 'challenge':   return '💪';
      case 'role':        return '👔';
      case 'achievement': return '🏆';
      case 'skill':       return '🎯';
      default:            return '📌';
    }
  };

  const kindBadge = (kind: EditableExperience['_kind']) => {
    if (kind === 'merge')   return <span className="rim-badge rim-badge--merge">UPDATING EXISTING</span>;
    if (kind === 'cleanup') return <span className="rim-badge rim-badge--cleanup">CONSOLIDATING DUPLICATES</span>;
    return <span className="rim-badge rim-badge--new">NEW</span>;
  };

  const subtitleText = () => {
    if (phase === 'loading') return 'Analyzing your resume and checking for duplicates…';
    if (phase === 'saving')  return 'Saving to your career memory…';
    if (phase === 'done')    return `${savedCount} operation${savedCount !== 1 ? 's' : ''} applied to your career timeline`;
    if (phase === 'error')   return 'Something went wrong';
    // review
    const parts: string[] = [];
    if (newCount)     parts.push(`${newCount} new`);
    if (mergeCount)   parts.push(`${mergeCount} merging with existing`);
    if (cleanupCount) parts.push(`${cleanupCount} consolidating duplicates`);
    return `${parts.join(' · ')} — review and adjust before saving`;
  };

  return (
    <div className="rim-overlay">
      <div className="rim-modal">

        {/* ── Header ── */}
        <div className="rim-header">
          <div>
            <h3 className="rim-title">Import from Resume</h3>
            <p className="rim-subtitle">{subtitleText()}</p>
          </div>
          <button type="button" className="rim-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* ── Body ── */}
        <div className="rim-body">

          {/* Loading */}
          {phase === 'loading' && (
            <div className="rim-loading">
              <div className="rim-steps">
                {steps.map(({ label }, i) => (
                  <div
                    key={label}
                    className={`rim-step ${stepDone(i) ? 'rim-step-done' : stepActive(i) ? 'rim-step-active' : 'rim-step-pending'}`}
                  >
                    <span className="rim-step-icon">
                      {stepDone(i) ? '✓' : stepActive(i) ? '⟳' : '○'}
                    </span>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <p className="rim-elapsed">{elapsed}s</p>
            </div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <div className="rim-error-state">
              <div className="rim-error-icon">⚠️</div>
              <p>{errorMsg}</p>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          )}

          {/* Done */}
          {phase === 'done' && (
            <div className="rim-done-state">
              <div className="rim-done-icon">🎉</div>
              <p className="rim-done-msg">
                Your career timeline has been updated with {savedCount} {savedCount !== 1 ? 'operations' : 'operation'}.
              </p>
              <button type="button" className="btn btn-primary" onClick={() => { onSaved(); onClose(); }}>
                View Timeline
              </button>
            </div>
          )}

          {/* Saving */}
          {phase === 'saving' && (
            <div className="rim-saving-state">
              <div className="rim-spinner" />
              <p className="rim-saving-label">
                Saving {savingProgress.current} of {savingProgress.total}…
              </p>
              <div className="rim-saving-bar-track">
                <div
                  className="rim-saving-bar-fill"
                  style={{ '--save-pct': `${savingProgress.total ? (savingProgress.current / savingProgress.total) * 100 : 0}%` } as React.CSSProperties}
                />
              </div>
            </div>
          )}

          {/* Review */}
          {phase === 'review' && (
            <div className="rim-review">
              {errorMsg && <div className="rim-inline-error">{errorMsg}</div>}
              <div className="rim-cards">
                {items.map((item) => {
                  if (item._removed) return null;
                  const isExpanded = expandedIds.has(item._id);
                  const hasStarContent = item.situation || item.task || item.action || item.result;
                  const allTags = [...(item.skills || []), ...(item.technologies || [])].slice(0, 8);
                  const totalTags = (item.skills?.length ?? 0) + (item.technologies?.length ?? 0);

                  return (
                    <div key={item._id} className={`rim-card rim-card--${item._kind}`}>
                      {/* Kind badge + remove */}
                      <div className="rim-card-top">
                        {kindBadge(item._kind)}
                        <button
                          type="button"
                          className="rim-card-remove"
                          onClick={() => removeItem(item._id)}
                          aria-label="Remove"
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>

                      {/* Meta row */}
                      {(item.company || item.role || item.startDate) && (
                        <div className="rim-card-meta">
                          <span className="rim-card-type-icon">{getTypeIcon(item.type)}</span>
                          {item.company && <span className="rim-card-company">{item.company}</span>}
                          {item.role && <span className="rim-card-role">{item.role}</span>}
                          {(item.startDate || item.endDate) && (
                            <span className="rim-card-dates">
                              {item.startDate || '?'} → {item.endDate || 'Present'}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Editable title */}
                      <input
                        className="rim-card-title-input"
                        value={item.title}
                        onChange={(e) => updateItem(item._id, 'title', e.target.value)}
                        placeholder="Title"
                        aria-label="Experience title"
                      />

                      {/* Editable description */}
                      <textarea
                        className="rim-card-desc-input"
                        value={item.description}
                        onChange={(e) => updateItem(item._id, 'description', e.target.value)}
                        placeholder="Description"
                        rows={2}
                        aria-label="Experience description"
                      />

                      {/* Skill / tech badges */}
                      {allTags.length > 0 && (
                        <div className="rim-card-tags">
                          {allTags.map((tag, i) => (
                            <span key={i} className="rim-tag">{tag}</span>
                          ))}
                          {totalTags > 8 && (
                            <span className="rim-tag rim-tag--more">+{totalTags - 8}</span>
                          )}
                        </div>
                      )}

                      {/* Expandable STAR */}
                      {hasStarContent && (
                        <button
                          type="button"
                          className="rim-card-expand-btn"
                          onClick={() => toggleExpand(item._id)}
                        >
                          {isExpanded ? '▲ Hide STAR details' : '▼ Show STAR details'}
                        </button>
                      )}
                      {isExpanded && hasStarContent && (
                        <div className="rim-star-section">
                          {(['situation', 'task', 'action', 'result'] as const).map((field) => {
                            const icons: Record<string, string> = { situation: '📍', task: '🎯', action: '⚡', result: '🎉' };
                            const labels: Record<string, string> = { situation: 'Situation', task: 'Task', action: 'Action', result: 'Result' };
                            return item[field] ? (
                              <div key={field} className="rim-star-row">
                                <span className="rim-star-label">{icons[field]} {labels[field]}</span>
                                <textarea
                                  className="rim-star-textarea"
                                  value={item[field] as string}
                                  onChange={(e) => updateItem(item._id, field, e.target.value)}
                                  rows={3}
                                  aria-label={labels[field]}
                                />
                              </div>
                            ) : null;
                          })}
                          {item.metrics && item.metrics.length > 0 && (
                            <div className="rim-star-row">
                              <span className="rim-star-label">📊 Metrics</span>
                              <ul className="rim-metrics-list">
                                {item.metrics.map((m, i) => <li key={i}>{m}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer (review only) ── */}
        {phase === 'review' && (
          <div className="rim-footer">
            <span className="rim-footer-count">
              {activeItems.length} of {items.length} selected
            </span>
            <div className="rim-footer-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={!activeItems.length}
              >
                Apply {activeItems.length} {activeItems.length !== 1 ? 'changes' : 'change'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
